import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';

// Context Providers
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { AuthProvider, useAuth } from './context/AuthContext';

// Components
import Navbar from './components/navigation/Navbar';
import Footer from './components/layout/Footer';
import ProtectedRoute from './components/ProtectedRoute';
import { StudentLayout, StudentNav } from './components/student';
import CommandPalette from './components/navigation/CommandPalette';

import SafeSuspense from './components/SafeSuspense';

// Pages
import Home from './pages/Home/Home';


const Tutorials = lazy(() => import('./pages/Tutorials/Tutorials'));
const PracticeLabs = lazy(() => import('./pages/PracticeLabs/PracticeLabs'));

const CoursesCatalog = lazy(() => import('./pages/Courses/CoursesCatalog'));
const CourseDetail = lazy(() => import('./pages/Courses/CourseDetail'));
const Login = lazy(() => import('./pages/Login/Login'));
const Register = lazy(() => import('./pages/Auth/Register'));
const OAuthCallback = lazy(() => import('./pages/Auth/OAuthCallback'));
const NotFound = lazy(() => import('./pages/NotFound/NotFound'));
const StudentHome = lazy(() => import('./pages/StudentHome'));
const Certificates = lazy(() => import('./pages/Certificates'));
const AdminLayout = lazy(() => import('./pages/Admin/AdminLayout'));
const AdminDashboard = lazy(() => import('./pages/Admin/Dashboard/Dashboard'));
const AdminCourses = lazy(() => import('./pages/Admin/Courses/Courses'));
const AdminCategories = lazy(() => import('./pages/Admin/Categories/Categories'));
const AdminUsers = lazy(() => import('./pages/Admin/Users/Users'));
const AdminTests = lazy(() => import('./pages/Admin/Tests/Tests'));
const AdminCertificates = lazy(() => import('./pages/Admin/Certificates/Certificates'));
const AdminReports = lazy(() => import('./pages/Admin/Reports/Reports'));
const AdminSettings = lazy(() => import('./pages/Admin/Settings/Settings'));
const AdminFinance = lazy(() => import('./pages/Admin/Finance/Finance'));
const MyDashboard = lazy(() => import('./pages/MyDashboard'));

const Profile = lazy(() => import('./pages/Profile/Profile'));
const Checkout = lazy(() => import('./pages/Checkout/Checkout'));
const AdminTestBuilder = lazy(() => import('./pages/Admin/Tests/TestBuilder'));
const PlacementTest = lazy(() => import('./pages/PlacementTest/PlacementTest'));
const LearningPath = lazy(() => import('./pages/LearningPath/LearningPath'));
const CareerPathList = lazy(() => import('./pages/CareerPaths/CareerPathList'));
const CareerPathDetail = lazy(() => import('./pages/CareerPaths/CareerPathDetail'));

const AdminCareerPaths = lazy(() => import('./pages/Admin/CareerPaths/CareerPaths'));
const AdminCareerPathForm = lazy(() => import('./pages/Admin/CareerPaths/CareerPathForm'));
const UserCareerPaths = lazy(() => import('./pages/StudentCareerPaths/UserCareerPaths'));

function App() {
  const location = useLocation();
  const { user } = useAuth();
  const { theme, setTheme } = useTheme();
  
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isUserDashboard = [
    '/user', '/my-dashboard', '/profile', '/checkout',
    '/playground', '/tutorials', '/learning-path',
    '/placement-test', '/my-learning', '/certificates',
    '/my-profile', '/settings', '/user/career-paths'
  ].some(p => location.pathname.startsWith(p));

  React.useEffect(() => {
    // Force light theme for public pages when user is not logged in
    const isPublicPage = !isAdminRoute && !isUserDashboard && !location.pathname.startsWith('/courses');
    if (!user && (isPublicPage || location.pathname.startsWith('/courses'))) {
      if (theme === 'dark') {
        setTheme('light');
      }
    }
  }, [user, isAdminRoute, isUserDashboard, theme, setTheme, location.pathname]);

  return (
    <div className="d-flex flex-column min-vh-100">
      <CommandPalette />
      {/* Show navigation for public pages and courses */}
      {(!isAdminRoute && !isUserDashboard) && (
        user ? <StudentNav studentName={user.username} /> : <Navbar />
      )}
      <main className="flex-grow-1 w-100">
            <SafeSuspense>
              <Suspense fallback={<div className="container py-5 text-center text-muted">Loading...</div>}>
                <Routes>
                  {/* Public Routes */}
                  <Route path="/" element={<Home />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/register" element={<Register />} />
                  <Route path="/auth/callback" element={<OAuthCallback />} />

                  {/* Public-ish Courses Routes */}
                  <Route path="/courses" element={<CoursesCatalog />} />
                  <Route path="/courses/:id" element={<CourseDetail />} />
                  <Route path="/career-paths" element={<CareerPathList />} />
                  <Route path="/career-path/:slug" element={<CareerPathDetail />} />

                  <Route element={<StudentLayout />}>
                    <Route
                      path="/checkout/:id"
                      element={
                        <ProtectedRoute>
                          <Checkout />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/user"
                      element={
                        <ProtectedRoute>
                          <MyDashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/user/home"
                      element={
                        <ProtectedRoute>
                          <StudentHome />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/user/certificates"
                      element={
                        <ProtectedRoute>
                          <Certificates />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/my-dashboard"
                      element={
                        <ProtectedRoute>
                          <Navigate to="/user" replace />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/tutorials/:id"
                      element={
                        <ProtectedRoute>
                          <Tutorials />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/playground"
                      element={
                        <ProtectedRoute>
                          <PracticeLabs />
                        </ProtectedRoute>
                      }
                    />
                    <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                    <Route
                      path="/user/career-paths"
                      element={
                        <ProtectedRoute>
                          <UserCareerPaths />
                        </ProtectedRoute>
                      }
                    />
                    <Route path="/user/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                    <Route path="/user" element={<ProtectedRoute><MyDashboard /></ProtectedRoute>} />
                    <Route path="/my-dashboard" element={<ProtectedRoute><MyDashboard /></ProtectedRoute>} />
                    <Route path="/placement-test" element={<ProtectedRoute><PlacementTest /></ProtectedRoute>} />
                    <Route path="/learning-path" element={<ProtectedRoute><LearningPath /></ProtectedRoute>} />
                  </Route>

                  {/* Admin Routes */}
                  <Route
                    path="/admin"
                    element={
                      <ProtectedRoute requireAdmin={true}>
                        <AdminLayout />
                      </ProtectedRoute>
                    }
                  >
                    <Route index element={<AdminDashboard />} />
                    <Route path="dashboard" element={<AdminDashboard />} />
                    <Route path="users" element={<AdminUsers />} />
                    <Route path="categories" element={<AdminCategories />} />
                    <Route path="courses" element={<AdminCourses />} />
                    <Route path="tests" element={<AdminTests />} />
                    <Route path="tests/builder/:id" element={<AdminTestBuilder />} />
                    <Route path="certificates" element={<AdminCertificates />} />
                    <Route path="finance" element={<AdminFinance />} />
                    <Route path="reports" element={<AdminReports />} />

                    <Route path="settings" element={<AdminSettings />} />
                    
                    <Route path="career-paths" element={<AdminCareerPaths />} />
                    <Route path="career-paths/new" element={<AdminCareerPathForm />} />
                    <Route path="career-paths/edit/:id" element={<AdminCareerPathForm />} />
                  </Route>





                  {/* Redirect root to home */}
                  <Route path="/" element={<Navigate to="/" replace />} />

                  {/* 404 - Not Found */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </SafeSuspense>
          </main>
          {!isAdminRoute && !isUserDashboard && <Footer />}
        </div>
  );
}

export default App;
